import { useCallback, useEffect, useState } from "react";

import { useQuranOptions } from "@/app/providers/QuranContextProvider";
import {
  QuranMushaf,
  QuranResponseType,
  QuranStateType,
  QuranTranslationType,
} from "../types";
import { useIndexedDB } from "./useIndexedDb";
import { loadTranslation } from "@/actions/load-translation";
import {
  QURAN_TRANSLATIONS_DATABASE_KEY_NAME,
  QURAN_TRANSLATIONS_DATABASE_NAME,
  Quran_TRANSLATIONS_DATABASE_TABLES,
} from "../constants";

const tanslationStatus = new Map<string, Promise<QuranTranslationType[]>>();

const useTranslations = (translationId: number, page: number) => {
  const { data, quranMushaf } = useQuranOptions();
  const [isTranslationLoaded, setIsTranslationLoaded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { saveRecords, getAllRecords, isDBConnecting } =
    useIndexedDB<QuranTranslationType>(
      QURAN_TRANSLATIONS_DATABASE_NAME,
      Quran_TRANSLATIONS_DATABASE_TABLES,
      QURAN_TRANSLATIONS_DATABASE_KEY_NAME,
    );

  const loadTranslations = useCallback(async () => {
    if (isDBConnecting) return;

    let translations: QuranTranslationType[] | undefined;
    let range: IDBKeyRange;
    let start: { surah: number; verse: number }, end: typeof start;
    let pages: QuranResponseType[] = [];

    if (quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16") {
      if (quranMushaf.lines == 15 || quranMushaf.lines == 16) {
        pages = data.current.IndoPak[quranMushaf.lines][page];
      }
    } else {
      pages = data.current.Uthmani[quranMushaf.type][page];
    }

    range = IDBKeyRange.bound(pages!![0].id, pages!![pages!!.length - 1].id);
    translations = await getAllRecords(translationId.toString(), range);

    if (translations && translations.length > 0) {
      cacheResult(
        translations,
        data,
        page,
        quranMushaf,
        translationId.toString(),
      );

      setIsTranslationLoaded(true);
      setIsLoading(false);
      return;
    }

    start = {
      surah: pages!![0].surah,
      verse: pages!![0].verse,
    };

    end = {
      surah: pages!![pages!!.length - 1].surah,
      verse: pages!![pages!!.length - 1].verse,
    };

    if (
      tanslationStatus.has(
        `${translationId}-${start.surah}:${start.verse}:${end.surah}:${end.verse}`,
      )
    ) {
      const cachedPromise = tanslationStatus.get(
        `${translationId}-${start.surah}:${start.verse}:${end.surah}:${end.verse}`,
      );
      if (cachedPromise) {
        cachedPromise.then(() => {
          setIsTranslationLoaded(true);
          setIsLoading(false);
        });
        return;
      }
    }

    try {
      const loadPromise = new Promise<Array<QuranTranslationType>>(
        async (resolve, reject) => {
          const resp = await fetch(
            `/api/get-translation?translation=${translationId}&from_surah=${start.surah}&from_verse=${start.verse}&to_surah=${end.surah}&to_verse=${end.verse}`,
          );

          const result = await resp.json();
          // const result = await loadTranslation(translationId, start, end);

          if ("error" in result) reject(result.error);
          else {
            resolve(result.res);
            return result;
          }
        },
      );

      tanslationStatus.set(
        `${translationId}-${start.surah}:${start.verse}:${end.surah}:${end.verse}`,
        loadPromise,
      );
      const result = await loadPromise;
      cacheResult(result, data, page, quranMushaf, translationId.toString());
      setIsLoading(false);
      setIsTranslationLoaded(true);

      saveRecords(translationId.toString(), result);

      // }
    } catch (e) {
      console.log("Error: ", e);
      setIsLoading(false);
      setIsTranslationLoaded(false);
    }
  }, [isDBConnecting]);

  useEffect(() => {
    if (isDBConnecting) return;
    if (isTranslationLoaded) return;

    loadTranslations();
  }, [loadTranslations, isDBConnecting]);

  return { isTranslationLoaded, isLoading, setIsTranslationLoaded };
};

const cacheResult = (
  result: QuranTranslationType[],
  data: QuranStateType["data"],
  page: number,
  quranMushaf: QuranMushaf,
  translationId: string,
) => {
  let index = 0;

  if (quranMushaf.type == "indopak15" || quranMushaf.type == "indopak16") {
    if (quranMushaf.lines == 15 || quranMushaf.lines == 16)
      for (; index < result.length; index++)
        data.current.IndoPak[quranMushaf.lines][page][index].translations = {
          ...data.current.IndoPak[quranMushaf.lines][page][index].translations,
          [translationId]: { ...result[index] },
        };
  } else
    for (; index < result.length; index++) {
      data.current.Uthmani[quranMushaf.type][page][index].translations = {
        ...data.current.Uthmani[quranMushaf.type][page][index].translations,
        [translationId]: { ...result[index] },
      };
    }
};

export default useTranslations;
